/**
 * LabUploadPage — external, unauthenticated lab-result upload portal.
 *
 * React migration of the AngularJS `lab_upload.html` / `lab_upload.js` (labUploadApp /
 * LabUploadController). See issue #273. Reproduces the original step machine:
 *   1  Upload a lab-result image        -> POST /auth/process_lab_result (multipart)
 *   2  Review AI analysis + manually match unmatched results to open orders
 *                                        -> POST /auth/confirm_lab_result (JSON)
 *   3  Success
 *   4  Manual-entry fallback (Cancel)
 *  -99 Error
 *
 * Served for /auth/lab_upload/:facilityCode via the existing react_shell view, exactly like the
 * Login / Forgot-Password pages. The backend contract (process_lab_result / confirm_lab_result) is
 * unchanged; facility_code is not part of the POST payload (the server derives open lab orders
 * tenant-wide), so it is captured only for parity/branding.
 */
import React, { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

type Step = 1 | 2 | 3 | 4 | 99 | -99;

interface LabValue {
  name?: string;
  value_with_units?: string;
}

interface AnalyzedLab {
  lab_name?: string;
  order_id?: number;
  patient_first_name?: string;
  patient_last_name?: string;
  patient_dob?: string;
  medical_record_number?: string;
  encounter_number?: string;
  lab_values?: LabValue[];
  lab_ai_analysis?: string;
}

interface AvailableOrder {
  order_id: number;
  order_name?: string;
  patient_first_name?: string;
  patient_last_name?: string;
}

interface LabResultAnalysis {
  lab_result?: {
    structured_analysis?: {
      open_labs?: AnalyzedLab[];
      lab_scanned_but_not_found?: AnalyzedLab[];
    };
    validation_errors?: string[];
    available_orders?: AvailableOrder[];
    open_orders_count?: number;
  };
}

const getCsrfToken = (): string => {
  const cookie = document.cookie
    .split('; ')
    .find((row) => row.startsWith('csrftoken='));
  return cookie ? cookie.split('=')[1] : '';
};

const cardStyle: React.CSSProperties = {
  border: '1px solid #ddd', margin: '10px 0', padding: 15, borderRadius: 5,
};

const buttonStyle: React.CSSProperties = {
  padding: '10px 20px', border: '1px solid #2c5aa0', borderRadius: 4,
  background: '#2c5aa0', color: 'white', cursor: 'pointer', fontSize: '1em',
};

const LoadingOverlay: React.FC<{ message: string }> = ({ message }) => (
  <div
    style={{
      position: 'fixed', top: 0, left: 0, width: '100%', height: '100%',
      background: 'rgba(0,0,0,0.8)', zIndex: 10000, display: 'flex',
      alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: '1.2em',
    }}
  >
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <div
        style={{
          border: '4px solid rgba(255,255,255,0.3)', borderTop: '4px solid white',
          borderRadius: '50%', width: 40, height: 40, animation: 'labspin 1s linear infinite',
        }}
      />
      <div style={{ marginTop: 16, textAlign: 'center' }}>{message}</div>
    </div>
    <style>{'@keyframes labspin { from { transform: rotate(0deg);} to { transform: rotate(360deg);} }'}</style>
  </div>
);

const Header: React.FC = () => (
  <div className="header" style={{ textAlign: 'center', padding: '20px 0' }}>
    <img id="auth-logo" src="/static/imgs/logo_black.png" alt="TECDoc" style={{ maxHeight: 60 }} />
  </div>
);

const LabDetails: React.FC<{ lab: AnalyzedLab }> = ({ lab }) => (
  <>
    <div style={{ marginBottom: 10 }}>
      <strong>Patient:</strong> {lab.patient_first_name} {lab.patient_last_name}{' '}
      <span style={{ color: '#666' }}>(DOB: {lab.patient_dob})</span>
      <br />
      <strong>MRN:</strong> {lab.medical_record_number}
      <strong style={{ marginLeft: 20 }}>Encounter:</strong> {lab.encounter_number}
    </div>
    <div style={{ marginBottom: 10 }}>
      <strong>Lab Values:</strong>
      <ul style={{ margin: '5px 0', paddingLeft: 20 }}>
        {(lab.lab_values ?? []).map((v, i) => (
          <li key={i} style={{ margin: '2px 0' }}>
            <strong>{v.name}</strong>: {v.value_with_units}
          </li>
        ))}
      </ul>
    </div>
  </>
);

const LabUploadPage: React.FC = () => {
  useParams(); // facilityCode is captured by the route but not needed in the POST payload
  const [step, setStep] = useState<Step>(1);
  const [loading, setLoading] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [analysis, setAnalysis] = useState<LabResultAnalysis | null>(null);

  const structured = analysis?.lab_result?.structured_analysis;
  const openLabs = structured?.open_labs ?? [];
  const unmatchedLabs = structured?.lab_scanned_but_not_found ?? [];
  const validationErrors = analysis?.lab_result?.validation_errors ?? [];
  const hasOpenLabs = openLabs.length > 0;
  const hasUnmatchedLabs = unmatchedLabs.length > 0;
  const totalOrdersCount = analysis?.lab_result?.open_orders_count ?? 0;

  const orderOptions = useMemo(
    () =>
      (analysis?.lab_result?.available_orders ?? []).map((order) => {
        const name = `${order.patient_first_name ?? ''} ${order.patient_last_name ?? ''}`.trim() || 'Unknown Patient';
        return {
          order_id: order.order_id,
          display_text: `${name} - ${order.order_name ?? 'Unknown Order'} (Order #${order.order_id ?? 'N/A'})`,
        };
      }),
    [analysis],
  );

  const submitLabResultImage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!file) return;
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append('lab_result_image', file);
      // No Content-Type header: the browser sets the multipart boundary itself.
      const resp = await fetch('/auth/process_lab_result', {
        method: 'POST',
        headers: { 'X-CSRFToken': getCsrfToken() },
        credentials: 'same-origin',
        body: fd,
      });
      const data = await resp.json();
      if (!resp.ok || data?.success === false) {
        setStep(-99);
        return;
      }
      setAnalysis(data);
      setStep(2);
    } catch {
      setStep(-99);
    } finally {
      setLoading(false);
    }
  };

  const updateUnmatchedOrder = (index: number, orderId: number) => {
    setAnalysis((prev) => {
      if (!prev) return prev;
      const next = structuredClone(prev);
      const list = next.lab_result?.structured_analysis?.lab_scanned_but_not_found;
      if (list && list[index]) list[index].order_id = orderId;
      return next;
    });
  };

  const confirmLabResult = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      const resp = await fetch('/auth/confirm_lab_result', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-CSRFToken': getCsrfToken() },
        credentials: 'same-origin',
        body: JSON.stringify({ lab_analysis: analysis }),
      });
      const data = await resp.json();
      if (resp.ok && data?.success) {
        setStep(3);
      } else {
        setStep(-99);
      }
    } catch {
      setStep(-99);
    } finally {
      setLoading(false);
    }
  };

  // --- Finish / error screens -------------------------------------------------
  if (step === 3 || step === 99) {
    return (
      <div id="main">
        <Header />
        <div style={{ textAlign: 'center', padding: 40 }}>
          <h2>Lab Results Successfully Uploaded</h2>
          <button type="button" style={buttonStyle} onClick={() => window.location.assign('/auth/lab_upload')}>
            Upload Another Lab Result
          </button>
        </div>
      </div>
    );
  }

  if (step === 4) {
    return (
      <div id="main">
        <Header />
        <div style={{ textAlign: 'center', padding: 40 }}>
          <h2>Please Manually Enter the Lab Results in TECDoc</h2>
          <button type="button" style={buttonStyle} onClick={() => window.location.assign('/auth/lab_upload')}>
            Upload Another Lab Result
          </button>
        </div>
      </div>
    );
  }

  if (step === -99) {
    return (
      <div id="main">
        <Header />
        <div style={{ maxWidth: 600, margin: '0 auto', padding: 20 }}>
          <h2>Unable to complete lab result upload</h2>
          <p>An error has occurred. Please manually enter the lab results in TECDoc.</p>
          <button type="button" style={buttonStyle} onClick={() => window.location.assign('/auth/lab_upload')}>
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // --- Step 1: upload ---------------------------------------------------------
  if (step === 1) {
    return (
      <div id="main">
        {loading && <LoadingOverlay message="We are processing the images, this will take a minute" />}
        <Header />
        <form onSubmit={submitLabResultImage} encType="multipart/form-data" style={{ maxWidth: 600, margin: '0 auto', padding: 20 }}>
          <h2>Upload Lab Result</h2>
          <div style={{ margin: '16px 0' }}>
            <label htmlFor="lab_result_image" style={{ display: 'block', marginBottom: 8 }}>Lab Result</label>
            <input
              id="lab_result_image"
              type="file"
              accept="image/*"
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            />
          </div>
          <button type="submit" style={buttonStyle} disabled={!file}>Continue →</button>
        </form>
      </div>
    );
  }

  // --- Step 2: review + manual matching --------------------------------------
  return (
    <div id="main">
      {loading && <LoadingOverlay message="Processing lab results..." />}
      <Header />
      <div style={{ maxWidth: 800, margin: '0 auto', padding: 20 }}>
        <h2>Lab Results Analysis</h2>

        {!hasOpenLabs && !hasUnmatchedLabs && (
          <div style={{ textAlign: 'center', padding: 40, background: '#f8f9fa', borderRadius: 5, marginBottom: 20 }}>
            <h3 style={{ color: '#6c757d', marginBottom: 10 }}>📄 No Lab Results Found</h3>
            <p style={{ color: '#6c757d', margin: 0 }}>
              No lab values could be extracted from the uploaded image, or no matching orders were found.
            </p>
          </div>
        )}

        {hasOpenLabs && (
          <div style={{ marginBottom: 30 }}>
            <h3 style={{ color: '#2c5aa0', borderBottom: '2px solid #2c5aa0', paddingBottom: 5 }}>
              📋 Orders to be Filled ({openLabs.length})
            </h3>
            {openLabs.map((lab, i) => (
              <div key={i} style={{ ...cardStyle, background: '#f9f9f9' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                  <h4 style={{ margin: 0, color: '#2c5aa0' }}>{lab.lab_name}</h4>
                  <span style={{ background: '#2c5aa0', color: 'white', padding: '2px 8px', borderRadius: 3, fontSize: '0.8em' }}>
                    Order #{lab.order_id}
                  </span>
                </div>
                <LabDetails lab={lab} />
                {lab.lab_ai_analysis && (
                  <div style={{ background: '#e8f4f8', padding: 10, borderRadius: 3, borderLeft: '4px solid #2c5aa0' }}>
                    <strong>Analysis:</strong> {lab.lab_ai_analysis}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {hasUnmatchedLabs && (
          <div style={{ marginBottom: 30 }}>
            <h3 style={{ color: '#d9534f', borderBottom: '2px solid #d9534f', paddingBottom: 5 }}>
              ⚠️ Results Without Matching Order ({unmatchedLabs.length})
            </h3>
            {unmatchedLabs.map((lab, i) => (
              <div key={i} style={{ ...cardStyle, background: '#fff3cd' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                  <h4 style={{ margin: 0, color: '#d9534f' }}>{lab.lab_name}</h4>
                  <span style={{ background: '#d9534f', color: 'white', padding: '2px 8px', borderRadius: 3, fontSize: '0.8em' }}>
                    No Matching Order
                  </span>
                </div>
                <LabDetails lab={lab} />
                {lab.lab_ai_analysis && (
                  <div style={{ background: '#f8d7da', padding: 10, borderRadius: 3, borderLeft: '4px solid #d9534f' }}>
                    <strong>Analysis:</strong> {lab.lab_ai_analysis}
                  </div>
                )}
                <div style={{ marginTop: 15, padding: 10, background: '#e8f4f8', borderRadius: 3, borderLeft: '4px solid #2c5aa0' }}>
                  <strong>Manual Order Matching:</strong>
                  <div style={{ marginTop: 8 }}>
                    <label style={{ display: 'block', marginBottom: 5 }}>Select an open order to match this lab result:</label>
                    <select
                      value={lab.order_id ?? 0}
                      onChange={(e) => updateUnmatchedOrder(i, Number(e.target.value))}
                      style={{ width: '100%', padding: 5, border: '1px solid #ccc', borderRadius: 3 }}
                    >
                      <option value={0}>-- Select an Order --</option>
                      {orderOptions.map((o) => (
                        <option key={o.order_id} value={o.order_id}>{o.display_text}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        <div style={{ background: '#e8f4f8', padding: 15, borderRadius: 5, marginBottom: 20 }}>
          <h4 style={{ marginTop: 0, color: '#2c5aa0' }}>📊 Summary</h4>
          <p style={{ margin: '5px 0' }}><strong>Total Open Orders Found:</strong> {totalOrdersCount}</p>
          <p style={{ margin: '5px 0' }}><strong>Orders Filled:</strong> {openLabs.length}</p>
          <p style={{ margin: '5px 0' }}><strong>Results Without Orders:</strong> {unmatchedLabs.length}</p>
        </div>

        {validationErrors.length > 0 && (
          <div style={{ marginBottom: 20, padding: 15, background: '#f8d7da', border: '1px solid #f5c6cb', borderRadius: 5 }}>
            <h4 style={{ color: '#721c24', marginTop: 0 }}>⚠️ Validation Errors Detected</h4>
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              {validationErrors.map((err, i) => (
                <li key={i} style={{ color: '#721c24' }}>{err}</li>
              ))}
            </ul>
          </div>
        )}

        <form onSubmit={confirmLabResult}>
          <div style={{ display: 'flex', gap: 15, justifyContent: 'center', marginTop: 20 }}>
            <button
              type="button"
              style={{ ...buttonStyle, background: 'white', color: '#6c757d', borderColor: '#6c757d' }}
              onClick={() => setStep(4)}
            >
              Cancel
            </button>
            <button type="submit" style={buttonStyle}>Continue →</button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default LabUploadPage;
